"use client";

import { useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/contexts/ToastContext";
import { isProfileCompleteForAccess } from "@/lib/profileCompletion";
import { buildOnboardingNotificationsHref } from "@/lib/navigation/onboardingRedirect";

interface ProfileCompletionGuardProps {
  children: React.ReactNode;
}

export default function ProfileCompletionGuard({
  children,
}: ProfileCompletionGuardProps) {
  const router = useRouter();
  const { user } = useAuth();
  const { showToast } = useToast();
  const hasShownToastRef = useRef(false);
  const lastProfileStateRef = useRef<string | null>(null);

  useEffect(() => {
    const checkProfileCompletion = async () => {
      if (!user) {
        hasShownToastRef.current = false;
        lastProfileStateRef.current = null;
        return;
      }

      const currentPath = window.location.pathname;
      if (
        currentPath.includes("/complete-profile") ||
        currentPath.startsWith("/onboarding") ||
        currentPath.startsWith("/verify-account")
      ) {
        return;
      }

      // Create a unique key for the current profile state
      const profileStateKey = `${user.id}-${user.is_profile_complete}-${user.phone}-${user.gender}-${user.name}-${user.location}-${user.location_state_id}-${user.location_lga_id}-${user.location_neighborhood_id}`;

      // If profile state hasn't changed, don't check again
      if (lastProfileStateRef.current === profileStateKey) {
        return;
      }

      // Update the last checked state
      lastProfileStateRef.current = profileStateKey;

      const isProfileIncomplete = !isProfileCompleteForAccess(user);

      if (isProfileIncomplete) {
        // Only show toast once per profile state
        if (!hasShownToastRef.current) {
          showToast("Please complete your profile to continue", "info");
          hasShownToastRef.current = true;
        }

        // Build the redirect URL
        const redirectUrl = `/complete-profile?email=${encodeURIComponent(user.email)}&redirectTo=${encodeURIComponent(currentPath)}`;

        // Only redirect if we're not already on the complete-profile page
        // Use replace to avoid adding to history stack
        if (window.location.pathname !== "/complete-profile") {
          router.replace(redirectUrl);
        }
        return;
      }

      if (!user.registrationOnboardingCompletedAt) {
        const onboardingHref = buildOnboardingNotificationsHref(currentPath);
        if (window.location.pathname !== onboardingHref.split("?")[0]) {
          router.replace(onboardingHref);
        }
        return;
      }

      // Profile is complete, reset the toast flag for future checks
      hasShownToastRef.current = false;
    };

    checkProfileCompletion();
  }, [user, router, showToast]);

  // Render children if user exists
  return <>{children}</>;
}
